import { type MaybeRefOrGetter, onUnmounted, toValue, watch } from 'vue';
import { isEditableTarget } from '@lib/shortcuts';

type PasteUploadHandler = (files: FileList) => void;

// Stack of registered handlers in mount order. The last entry is the most
// recently mounted file dropper, which is the one that should receive a paste
// when several are open at once (e.g. a popper opened on top of a page).
const handlers: PasteUploadHandler[] = [];
let bound = false;

function handlePaste(event: ClipboardEvent) {
  // Already claimed, e.g. by a table pasting a range of cells. Spreadsheets put an
  // image on the clipboard next to the text, which would otherwise be uploaded.
  if (event.defaultPrevented) return;

  const clipboard = event.clipboardData;
  const files = clipboard?.files;
  if (!files || files.length === 0) return;

  // Rich sources (Excel, Word, web pages) put both text and a rendered image on
  // the clipboard. When the user is typing in a field, they meant the text.
  if (isEditableTarget(event.target) && clipboard.getData('text/plain')) return;

  const handler = handlers[handlers.length - 1];
  if (!handler) return;

  event.preventDefault();
  handler(files);
}

function register(handler: PasteUploadHandler) {
  handlers.push(handler);
  if (!bound && typeof document !== 'undefined') {
    document.addEventListener('paste', handlePaste);
    bound = true;
  }
}

function unregister(handler: PasteUploadHandler) {
  const index = handlers.lastIndexOf(handler);
  if (index !== -1) handlers.splice(index, 1);
  if (handlers.length === 0 && bound && typeof document !== 'undefined') {
    document.removeEventListener('paste', handlePaste);
    bound = false;
  }
}

/**
 * Registers a handler that receives files pasted (Ctrl-V) — e.g. an image
 * copied from a screenshot tool — while the calling component is mounted.
 *
 * When several handlers are registered at once (multiple open file droppers),
 * only the most recently mounted one receives the paste, matching the user's
 * focus on the latest-opened dropper.
 */
export function usePasteUpload(handler: PasteUploadHandler, enabled: MaybeRefOrGetter<boolean> = true) {
  let registered = false;

  const stop = watch(
    () => toValue(enabled),
    (isEnabled) => {
      if (isEnabled && !registered) {
        register(handler);
        registered = true;
      } else if (!isEnabled && registered) {
        unregister(handler);
        registered = false;
      }
    },
    { immediate: true }
  );

  onUnmounted(() => {
    stop();
    if (registered) {
      unregister(handler);
      registered = false;
    }
  });
}
